import React, { useState, useEffect, useRef } from "react";
import { MenuTag } from "src/interfaces/menu.interface";

interface MultiSelectTagsProps {
  tags: MenuTag[];
  selectedTagIds: string[];
  onChange: (selectedTagIds: string[]) => void;
  placeholder?: string;
}

const MultiSelectTags: React.FC<MultiSelectTagsProps> = ({
  tags,
  selectedTagIds,
  onChange,
  placeholder = "Select tags",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const groupedTags = tags.reduce((acc, tag) => {
    if (!acc[tag.category]) {
      acc[tag.category] = [];
    }
    acc[tag.category].push(tag);
    return acc;
  }, {} as Record<string, MenuTag[]>);

  const handleToggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const getSelectedTags = () => {
    return tags.filter((tag) => selectedTagIds.includes(tag.correlationId));
  };

  const selectedTags = getSelectedTags();

  const handleRemoveTag = (e: React.MouseEvent, tagId: string) => {
    e.stopPropagation();
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  return (
    <div className="w-full relative mt-[6px]" ref={dropdownRef}>
      <div
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
        tabIndex={0}
        className="flex border border-[#F1F1F2] items-center justify-between min-h-[42px] px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsDropdownOpen((o) => !o);
          }
        }}
      >
        <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {selectedTags.length === 0 ? (
            <span className="text-xs text-[#9B9B9B]">{placeholder}</span>
          ) : (
            selectedTags.map((tag) => (
              <span
                key={tag.correlationId}
                className="inline-flex items-center gap-1 text-xs bg-[#FD76571A] text-[#252525] pl-2.5 pr-1 py-1 rounded-lg border border-[#FD765733]"
              >
                <span className="truncate max-w-[120px]">{tag.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${tag.name}`}
                  onClick={(e) => handleRemoveTag(e, tag.correlationId)}
                  className="shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-[#FD765740] text-[#666] hover:text-[#252525] transition-colors"
                >
                  <span className="leading-none">×</span>
                </button>
              </span>
            ))
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isDropdownOpen && (
        <div  className="absolute z-50 left-0 right-0 mt-2 bg-white  rounded-xl dropdown-shadow max-h-[392px] overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm mb-4 text-[#828282]">Select Category</h3>

            {/* None option to clear all tags */}
            <div className="mb-4 pb-4 border-b border-[#F1F1F2]">
              <label
                role="checkbox"
                aria-checked={selectedTagIds.length === 0}
                tabIndex={0}
                onClick={() => onChange([])}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange([]);
                  }
                }}
                className="flex items-center justify-between cursor-pointer group"
              >
                <span className="text-sm capitalize font-[300] text-[#A3A3A3] group-hover:text-gray-900 transition-colors">
                  None
                </span>
                <div
                  className={`w-4 h-4 border-[#A3A3A3] rounded border-[2px] flex items-center justify-center transition-all ${
                    selectedTagIds.length === 0
                      ? "bg-white text-primary border-primary"
                      : "border-gray-300 group-hover:border-gray-400"
                  }`}
                >
                  {selectedTagIds.length === 0 && (
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </label>
            </div>

            {Object.entries(groupedTags).map(([category, categoryTags]) => (
              <div key={category} className="mb-6 last:mb-0">
                <h4 className="text-sm text-[#252525] mb-3">{category}</h4>
                <div className="space-y-4 pl-2">
                  {categoryTags.map((tag) => {
                    const isSelectedTag = selectedTagIds.includes(
                      tag.correlationId
                    );
                    return (
                      <label
                        role="checkbox"
                        aria-checked={isSelectedTag}
                        tabIndex={0}
                        onClick={() => handleToggleTag(tag.correlationId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleToggleTag(tag.correlationId);
                          }
                        }}
                        key={tag.correlationId}
                        className="flex items-center justify-between cursor-pointer group"
                      >
                        <span
                          className={`text-sm capitalize font-[300] text-[#A3A3A3] group-hover:text-gray-900 transition-colors`}
                        >
                          {tag.name}
                        </span>
                        <div
                          className={`w-4 h-4 border-[#A3A3A3] rounded border-[2px]  flex items-center justify-center transition-all ${
                            isSelectedTag
                              ? "bg-white text-primary border-primary"
                              : "border-gray-300 group-hover:border-gray-400"
                          }`}
                        >
                          {isSelectedTag && (
                            <svg
                              className="w-4 h-4 text-primary"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectTags;
